<template>
  <SkeletonTableLoader :items-length="items.length">
    <v-container>
      <v-row class="ma-1">
        <v-spacer />
        <v-btn
          rounded
          dense
          small
          color="primary"
          @click="handleCreateOrEdit()"
          :disabled="!canUserEdit"
        >
          <v-icon> mdi-plus </v-icon>
          {{ $t(`variation.add.${type}`) }}
        </v-btn>
      </v-row>
      <VariationsTable
        @EDIT_VARIATION="handleCreateOrEdit"
        @ADD_COMMENT_TO_VARIATION="handleAddComment"
        @ADD_DOCUMENT_TO_VARIATION="handleAddDocument"
        @DELETE_VARIATION="handleDelete"
        :variations="localItems"
        @DELETE_COMMENT="handleDeleteComment"
        @SAVE_HISTORY_COMMENT="handleSaveHistoryComment"
        :type="type"
        :chosen-currency="contract.chosenCurrency"
        :parts-list="partsList"
        :isMultiCurrency="contract.isMultiCurrency"
      />
      <v-row class="ma-1">
        <v-spacer />
        <v-btn
          rounded
          dense
          small
          color="success"
          @click="handleOpenImportMilestones"
        >
          <v-icon small class="mr-1">mdi-file-excel-outline</v-icon>
          {{ $t(`variation.import.title.${this.type}`) }}
        </v-btn>
      </v-row>
    </v-container>
  </SkeletonTableLoader>
</template>

<script>
import {
  upgradeVariation,
  commentVariation,
  deleteVariationComment,
  updateVariationHistoryComment,
  addDocumentToVariation,
  deleteVariation
} from '@/services'
import { mapState } from 'vuex'
import { DEFAULT_VARIATION } from '@/helpers'

export default {
  name: 'CommonVariationView',
  props: {
    contract: {
      type: Object,
      default: function () {
        return {}
      }
    },
    items: Array,
    type: String,
    reporter: String,
    formFields: {
      type: Array,
      default: () => ([])
    }
  },
  data () {
    return {
      localItems: []
    }
  },
  computed: {
    ...mapState({
      canUserEdit: state => state.userPermissionsToContract.canUserEdit
    }),
    partsList () {
      return this.contract.parts.map(part => {
        const currencyData = this.contract.currencies.find(i => i.key === part.currency) || this.contract.currencies.find(i => i.value === 1)
        return {
          ...part,
          currencyData,
          formattedPrice: part.signedPriceCents
            ? this.$options.filters.formatCurrency(part.signedPriceCents, { key: part.currency, value: 1 })
            : null
        }
      })
    }
  },
  methods: {
    refreshVariationFromResponse (variation) {
      const updatedVariation =
        variation?.variation ||
        variation?.data?.variation ||
        variation?.data ||
        variation
      if (!updatedVariation || !updatedVariation.id) {
        return false
      }
      const index = this.localItems.findIndex(item => `${item.id}` === `${updatedVariation.id}`)
      if (index === -1) {
        return false
      }
      const updatedItems = [...this.localItems]
      updatedItems.splice(index, 1, {
        ...updatedItems[index],
        ...updatedVariation
      })
      this.localItems = updatedItems
      return true
    },
    async handleDelete (item) {
      this.$eventBus.$emit('OPEN_CONFIRM_MODAL', {
        scope: {
          message: this.$t('common.deleteConfirm', { name: item.no })
        },
        handlers: {
          yes: async () => {
            await deleteVariation(item)
            this.$emit('FETCH_DATA')
          }
        }
      })
    },
    handleOpenImportMilestones () {
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'VariationsImport',
        options: { maxWidth: 1500, minWidth: 500 },
        scope: {
          contract: this.contract,
          variations: this.items,
          type: this.type,
          chosenCurrency: this.contract.chosenCurrency
        },
        handlers: {
          FETCH_DATA: () => {
            this.$emit('FETCH_DATA')
            this.$eventBus.$emit('FETCH_CONTRACT')
          }
        }
      })
    },
    handleCreateOrEdit (variation = DEFAULT_VARIATION[this.type]) {
      variation.contractId = this.contract.id || variation.contractId
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'VariationForm',
        fields: this.formFields,
        options: { maxWidth: 1050 },
        scope: {
          value: variation,
          area: this.contract.area,
          type: this.type,
          reporter: this.reporter,
          parts: this.partsList,
          formFields: this.formFields,
          isMultiCurrency: this.contract.isMultiCurrency,
          baseCurrency: this.contract.currencies.find(i => i.value === 1),
          currencies: this.contract.currencies
        },
        handlers: {
          update: () => this.$emit('FETCH_DATA'),
          UPGRADE_VARIATION: this.handleUpgradeVariation
        }
      })
    },
    handleAddDocument (variation) {
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'DocumentForm',
        options: { maxWidth: 550 },
        scope: {
          title: this.$t('variation.addDocument', { name: variation.no })
        },
        handlers: {
          SAVE_DOCUMENT: async (document) => {
            await addDocumentToVariation(document, this.$route.params.contractId, variation.id)
            this.$eventBus.$emit('CLOSE_MODAL')
            this.$emit('FETCH_DATA')
          }
        }
      })
    },
    handleAddComment (variation) {
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'VariationCommentForm',
        options: { maxWidth: 550 },
        scope: {
          title: this.$t('variation.addComment'),
          value: '',
          submitLabel: this.$t('common.add'),
          showColorPicker: true
        },
        handlers: {
          input: ({ comment, color }) => this.addCommentToVariation(variation, comment, color)
        }
      })
    },
    async handleDeleteComment (variation) {
      this.$eventBus.$emit('OPEN_CONFIRM_MODAL', {
        scope: {
          message: this.$t('variation.deleteCommentConfirm')
        },
        handlers: {
          yes: async () => {
            await deleteVariationComment(this.contract.id, variation.id)
            this.$emit('FETCH_DATA')
          }
        }
      })
    },
    handleSaveHistoryComment ({ variation, historyEntry }) {
      const hasUserComment = historyEntry.userComment && historyEntry.userComment.trim()
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'VariationCommentForm',
        options: { maxWidth: 550 },
        scope: {
          title: this.$t(hasUserComment ? 'variation.editComment' : 'variation.addComment'),
          value: historyEntry.userComment || '',
          submitLabel: this.$t(hasUserComment ? 'common.save' : 'variation.addComment')
        },
        handlers: {
          input: (userComment) => this.saveHistoryComment(variation, historyEntry, userComment)
        }
      })
    },
    async handleUpgradeVariation (variation) {
      await upgradeVariation(variation)
      this.$emit('FETCH_DATA')
      this.$eventBus.$emit('CLOSE_MODAL')
    },
    async saveHistoryComment (variation, historyEntry, userComment) {
      const updatedVariation = await updateVariationHistoryComment(
        this.contract.id,
        variation.id,
        historyEntry.id,
        userComment
      )
      this.refreshVariationFromResponse(updatedVariation)
      this.$emit('FETCH_DATA')
      this.$eventBus.$emit('CLOSE_MODAL')
    },
    async addCommentToVariation (variation, comment, color) {
      const updatedVariation = await commentVariation(
        this.contract.id,
        variation.id,
        {
          user: this.auth.account?.name,
          comment,
          createdAt: new Date(),
          type: 'comment',
          color: color || 'secondary'
        }
      )
      this.refreshVariationFromResponse(updatedVariation)
      this.$emit('FETCH_DATA')
      this.$eventBus.$emit('CLOSE_MODAL')
    }
  },
  watch: {
    items: {
      immediate: true,
      handler (newItems) {
        this.localItems = [...newItems]
      }
    }
  }
}
</script>
